(function () {
  const addDays = (date, days) => {
    const result = new Date(date);
    result.setDate(result.getDate() + parseInt(days, 10));
    return result;
  };

  const localizationConfig = {
    locale: 'en-US',
    timeZone: 'America/Los_Angeles',
    timeStyle: 'short',
    hour12: false,
  };

  const formatDate = (date) => {
    return date.toLocaleDateString(localizationConfig.locale, {
      timeZone: localizationConfig.timeZone,
      month: 'short',
      day: 'numeric',
    });
  };

  class lgDeliveryEstimate extends HTMLElement {
    constructor() {
      super();
      this.today = new Date();
      this.initialized = false;
      this.observer = new IntersectionObserver(this.handleIntersect.bind(this), { threshold: 0.1 });
      this.observer.observe(this);
    }

    handleIntersect(entries, observer) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.connectedCallback();
          observer.disconnect();
        }
      });
    }

    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;

      this.cacheDOMElements();
      this.initVariants();
      this.calculateEstimate();

      document.querySelector(`#${this.dataset.productFormId}`).addEventListener('variant:changed', this.debounce(this.onVariantChange.bind(this), 300));
    }

    cacheDOMElements() {
      this.container = this.querySelector('.js-estimated-delivery');
      this.wrapper = this.closest('.js-estimated-delivery-wrapper');
      this.statusIconContainer = this.querySelector('.js-delivery-status-icon');
      this.statusContainer = this.querySelector('.js-delivery-status');
      this.messageContainer = this.querySelector('.js-delivery-message');
      this.linkTrigger = this.querySelector('.js-delivery-link');
      this.input = this.querySelector('.js-delivery-input');
    }

    initVariants() {
      const { variants, variant, fallbackText, preorderText, readyToShipText, madeToOrderText, linkText, settingsJson } = this.dataset;
      const settings = JSON.parse(decodeURIComponent(settingsJson).replace(/\+/g, ' '));
      this.cutoffTime = settings.cutoffTime;
      this.saturdayShipping = settings.saturdayShipping;
      this.sundayShipping = settings.sundayShipping;
      this.holidays = new Set(settings.holidays);
      this.excludedProducts = settings.excludedProducts;

      this.variantStates = JSON.parse(decodeURIComponent(variants).replace(/\+/g, ' '));
      this.variant = JSON.parse(decodeURIComponent(variant).replace(/\+/g, ' '));
      this.fallbackText = fallbackText;
      this.preorderText = preorderText;
      this.readyToShipText = readyToShipText;
      this.madeToOrderText = madeToOrderText;
      this.linkText = linkText;
    }

    getTimezoneOffset() {
      const atTime = new Date();
      const timeZone = localizationConfig.timeZone;
      const localizedTime = new Date(atTime.toLocaleString(localizationConfig.locale, { timeZone }));
      const utcTime = new Date(atTime.toLocaleString(localizationConfig.locale, { timeZone: 'UTC' }));
      const diff = Math.round((localizedTime - utcTime) / (60 * 60 * 1000));

      return diff >= 0 ? `+${String(diff).padStart(2, '0')}` : `${String(diff).padStart(2, '0')}`;
    }

    debounce(func, wait) {
      let timeout;
      return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => func.apply(this, args), wait);
      };
    }

    onVariantChange(e) {
      const { variant } = e.detail;
      this.variant = variant;

      // Update the status container based on the new variant's status
      const variantState = this.variantState();
      if (this.statusContainer.innerHTML !== variantState.label) {
        this.statusContainer.innerHTML = variantState.label;
      }

      this.calculateEstimate();
    }

    currentTime() {
      const time = new Date().toLocaleString(localizationConfig.locale, {
        timeZone: localizationConfig.timeZone,
        timeStyle: localizationConfig.timeStyle,
        hour12: localizationConfig.hour12,
      });
      return parseInt(time.split(':')[0], 10);
    }

    calcDates(start, edd, cutoff = true) {
      let diff = edd;
      let index = 0;
      const cutoffExceeded = this.currentTime() >= this.cutoffTime;

      if (cutoff && cutoffExceeded) {
        diff++;
      }

      while (index <= diff) {
        const date = addDays(start, index);
        if (this.holidays.has(date.toISOString().slice(0, 10)) || date.getDay() === 0 || (!this.saturdayShipping && date.getDay() === 6) || (!this.sundayShipping && date.getDay() === 7)) {
          diff++;
        }
        index++;
      }

      return formatDate(addDays(start, diff));
    }

    variantState() {
      const variantState = this.variantStates.find((e) => e.id === this.variant.id);
      console.log('Current Variant State:', variantState);

      if (!variantState || variantState.label === 'sold-out' || !this.variant.available) {
        return {
          label: 'sold-out',
          text: this.fallbackText,
          eddStatus: variantState ? variantState.eddStatus : '',
          link: this.fallbackText,
        };
      }

      const getDateRange = (edd) => {
        const [min, max = min] = edd.split('-').map((date) => parseInt(date || 0, 10));
        return { min, max };
      };

      const generateState = (label, text, link, edd, incomingDate) => {
        let startDate = this.today;
        if (incomingDate) {
          startDate = new Date(incomingDate + 'T00:00:00.000Z');
        }
        return {
          label,
          text,
          link,
          eddStatus: variantState.eddStatus,
          ...getDateRange(edd),
          start: startDate,
        };
      };

      switch (variantState.label) {
        case 'always-pre-order':
          if (variantState.eddPreorder) {
            let state = generateState('always-pre-order', this.preorderText, this.linkText, variantState.eddPreorder);
            state.eddStatus = 'Pre-Order';
            return state;
          }
          break;

        case 'pre-order':
          if (variantState.eddPreorder) {
            return generateState('pre-order', this.preorderText, this.linkText, variantState.eddPreorder, variantState.nextIncomingDate);
          }
          break;

        case 'ready-to-ship':
          if (variantState.eddReadyToShip) {
            return generateState('ready-to-ship', this.readyToShipText, this.linkText, variantState.eddReadyToShip);
          }
          break;

        case 'made-to-order':
          if (variantState.eddMadeToOrder) {
            return generateState('made-to-order', this.madeToOrderText, this.linkText, variantState.eddMadeToOrder);
          }
          break;
      }

      return {
        label: 'fallback',
        text: this.fallbackText,
        eddStatus: variantState.eddStatus,
        link: this.fallbackText,
      };
    }

    calculateEstimate() {
      const variantState = this.variantState();
      console.log('Variant State:', variantState);
      console.log('Estimate Dates:', { text: variantState.text, link: variantState.link, min: variantState.min, max: variantState.max, start: variantState.start });

      if (variantState.label === 'sold-out') {
        this.updateDOM(variantState.label, variantState.link, '', '');
        this.applyWrapperClass('sold-out');
      } else if (variantState.label === 'fallback') {
        this.updateDOM(variantState.text, variantState.link, '', ''); // Make fallback message visible
        this.applyWrapperClass('fallback');
      } else {
        const { text, link, min, max, start } = variantState;
        const cutoff = !(variantState.label === 'pre-order' && variantState.nextIncomingDate);
        const estimateDate = min === max ? this.calcDates(start, min, cutoff) : `${this.calcDates(start, min, cutoff)} - ${this.calcDates(start, max, cutoff)}`;

        const sanitizedText = text.replace(/:$/, '');
        console.log('Final Estimate Date:', estimateDate);
        this.updateDOM(`${text} <strong><span>${estimateDate}<span></strong>`, link, `properties[${sanitizedText}]`, estimateDate);
        this.applyWrapperClass('status');
      }
      this.removeAttribute('hidden');
    }

    updateDOM(message, linkText, inputName, inputValue) {
      const { messageContainer, linkTrigger, input, statusContainer, statusIconContainer } = this;

      console.log('Updating DOM with:', { message, linkText, inputName, inputValue });

      // Get the variant state to dynamically update the status and message separately
      const variantState = this.variantState();
      const variantStatus = variantState.eddStatus || '';

      // Hide the status container for 'pre-order' and 'always-pre-order' labels
      if (variantState.label === 'pre-order' || variantState.label === 'always-pre-order') {
        statusContainer.innerHTML = '';
      } else {
        // Show the status container and populate it for other states
        statusContainer.style.display = '';
        if (statusContainer.innerHTML !== variantStatus) {
          statusContainer.innerHTML = variantStatus;
        }
      }

      if (variantState.label === 'pre-order' || variantState.label === 'always-pre-order') {
        statusContainer.classList.remove('inventory--high');
        statusIconContainer.classList.remove('inventory--high'); // Remove class from status icon
      } else if (variantState.label === 'ready-to-ship') {
        statusContainer.classList.add('inventory--high');
        statusIconContainer.classList.add('inventory--high'); // Add class to status icon
      } else {
        statusContainer.classList.remove('inventory--high');
        statusIconContainer.classList.remove('inventory--high'); // Remove class from status icon
      }

      // Update the message container
      if (messageContainer.innerHTML !== message) {
        messageContainer.innerHTML = message;
      }

      // Update the link trigger container (ensure it's not hidden anymore)
      linkTrigger.style.display = '';
      if (linkTrigger.innerHTML !== linkText) {
        linkTrigger.innerHTML = linkText;
      }

      // Update the input element
      if (input.name !== inputName) {
        input.name = inputName;
      }

      if (input.value !== inputValue) {
        input.value = inputValue;
      }
    }

    applyWrapperClass() {
      const variantState = this.variantState();

      if (this.wrapper) {
        const possibleStates = ['sold-out', 'fallback', 'always-pre-order', 'pre-order', 'ready-to-ship', 'made-to-order'];
        const baseClass = 'estimated-delivery-variant';

        possibleStates.forEach((state) => {
          this.wrapper.classList.remove(`${baseClass}-${state}`);
        });

        if (variantState && variantState.label) {
          this.wrapper.classList.add(`${baseClass}-${variantState.label}`);
        }
      }
    }
  }

  if ('customElements' in window) {
    customElements.define('lg-delivery-estimate', lgDeliveryEstimate);
  }
})();
